/**
 * Your letterhead.
 *
 * Six lines and a logo, typed once, that appear on every document the business
 * ever makes. It is the least glamorous screen in the product and close to the
 * most important: every starter binds its header to these fields, and until this
 * screen existed there was nothing to fill them with — so every invoice came out
 * with a blank where the company's name belongs and no way to find out why.
 *
 * Two things about the shape of it.
 *
 * **There is a preview, and it is the point.** Somebody filling in a form about
 * how their documents look should be able to see their documents change. The panel
 * on the right is the same block the starters draw — logo, name, address, contact
 * lines — in the brand's own colour, so the answer to "what will this look like"
 * is on screen before anything is saved.
 *
 * **More than one is normal.** Two trading names, an agency invoicing under a
 * client's letterhead, a regulated arm and an unregulated one. One of them is the
 * default, which is what a new design starts with, and any design can be switched
 * to another from the designer.
 */
import { useEffect, useRef, useState } from "react";
import { api, type Brand as BrandRow, type Look } from "../api.js";
import { reportFailure } from "../App.js";
import { Button, Card, Chip, Confirm, Field, Note, Skeleton, toast } from "../ui/index.js";

/** Matches the service's cap, so the refusal happens before the upload rather than after. */
const LOGO_BYTES = 400 * 1024;
const LOGO_TYPES = ["image/png", "image/jpeg", "image/webp", "image/svg+xml"];

type Draft = Omit<BrandRow, "updatedAt">;

export function Brand() {
  const [brands, setBrands] = useState<BrandRow[] | null>(null);
  const [looks, setLooks] = useState<Look[]>([]);
  const [chosen, setChosen] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const file = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { brands: loaded, looks: available } = await api.brands();
        if (cancelled) return;
        setBrands(loaded);
        setLooks(available);
        const first = loaded.find((b) => b.isDefault) ?? loaded[0];
        if (first) {
          setChosen(first.id);
          setDraft(first);
        }
      } catch (e) {
        if (!cancelled) reportFailure(e, "We couldn't load your business details.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const open = (brand: BrandRow) => {
    setChosen(brand.id);
    setDraft(brand);
  };

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => setDraft((d) => (d ? { ...d, [key]: value } : d));

  const addAnother = async () => {
    setSaving(true);
    try {
      const { brand } = await api.createBrand({ name: "New details" });
      const { brands: loaded } = await api.brands();
      setBrands(loaded);
      open(brand);
      toast("Added. Fill it in and save.", "good");
    } catch (e) {
      reportFailure(e, "We couldn't add that.");
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const { brand } = await api.updateBrand(draft.id, {
        name: draft.name.trim() || draft.company.trim() || "Business details",
        company: draft.company,
        address: draft.address,
        email: draft.email,
        phone: draft.phone,
        website: draft.website,
        registration: draft.registration,
        logo: draft.logo,
        themeId: draft.themeId,
        colour: draft.colour,
        ...(draft.isDefault ? { isDefault: true } : {})
      });
      const { brands: loaded } = await api.brands();
      setBrands(loaded);
      setDraft(brand);
      toast("Saved. Your documents will use this.", "good");
    } catch (e) {
      reportFailure(e, "We couldn't save that.");
    } finally {
      setSaving(false);
    }
  };

  /**
   * The logo, read in this tab.
   *
   * A data URI rather than an upload, for the reason the service documents: a
   * document must never depend on the network to draw its own letterhead. The
   * limits are checked here as well as there, so a photograph dragged in by
   * mistake is refused immediately instead of after a slow upload.
   */
  const onLogo = (picked: File) => {
    if (!LOGO_TYPES.includes(picked.type)) {
      toast("A logo can be a PNG, a JPEG, a WebP or an SVG.", "bad");
      return;
    }
    if (picked.size > LOGO_BYTES) {
      toast(`That file is ${Math.round(picked.size / 1024)} KB, and a logo has to be under ${Math.round(LOGO_BYTES / 1024)} KB.`, "bad");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => set("logo", String(reader.result));
    reader.onerror = () => toast("We couldn't read that image.", "bad");
    reader.readAsDataURL(picked);
  };

  if (!brands) {
    return (
      <div className="wrap stack" style={{ gap: 20 }}>
        <div className="skeleton" style={{ height: 36, width: 240 }} />
        <Skeleton rows={2} height={200} />
      </div>
    );
  }

  return (
    <div className="wrap stack" style={{ gap: 22 }}>
      <div className="head">
        <div className="head__title">
          <span className="eyebrow">Your brand</span>
          <h1>How your documents are headed</h1>
          <p className="head__lede">
            Your name, your address and your logo. Every design uses these, so filling them in once puts them on everything you make.
          </p>
        </div>
        <div className="head__actions">
          <Button onClick={() => void addAnother()} busy={saving} icon="plus">
            Add another
          </Button>
        </div>
      </div>

      {brands.length > 1 ? (
        <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
          {brands.map((brand) => (
            <button key={brand.id} type="button" className={`tile${brand.id === chosen ? " tile--chosen" : ""}`} style={{ padding: "10px 14px", width: "auto" }} onClick={() => open(brand)}>
              <div className="row" style={{ gap: 8, alignItems: "center" }}>
                <strong>{brand.name}</strong>
                {brand.isDefault ? <Chip tone="good">Default</Chip> : null}
              </div>
            </button>
          ))}
        </div>
      ) : null}

      {draft ? (
        <div className="grid grid--2" style={{ alignItems: "start", gap: 20 }}>
          <Card>
            <div className="card__body stack" style={{ gap: 14 }}>
              <Field label="What this set of details is called" id="brand-name" hint="Only you see this. It is how you pick between them.">
                <input id="brand-name" className="input" value={draft.name} maxLength={200} onChange={(e) => set("name", e.target.value)} />
              </Field>

              <Field label="Business name" id="brand-company" hint="Printed at the top of every document.">
                <input id="brand-company" className="input" value={draft.company} maxLength={200} onChange={(e) => set("company", e.target.value)} />
              </Field>

              <Field label="Address" id="brand-address" hint="One line or several — write it as you would on a letter.">
                <textarea id="brand-address" className="input" rows={3} value={draft.address} maxLength={400} onChange={(e) => set("address", e.target.value)} />
              </Field>

              <div className="grid grid--2" style={{ gap: 12 }}>
                <Field label="Email" id="brand-email">
                  <input id="brand-email" className="input" value={draft.email} maxLength={200} onChange={(e) => set("email", e.target.value)} />
                </Field>
                <Field label="Telephone" id="brand-phone">
                  <input id="brand-phone" className="input" value={draft.phone} maxLength={80} onChange={(e) => set("phone", e.target.value)} />
                </Field>
              </div>

              <Field label="Website" id="brand-website">
                <input id="brand-website" className="input" value={draft.website} maxLength={200} onChange={(e) => set("website", e.target.value)} />
              </Field>

              <Field
                label="Small print"
                id="brand-registration"
                hint="A company number, a VAT number, a regulator. It sits at the foot of the page."
              >
                <textarea id="brand-registration" className="input" rows={2} value={draft.registration} maxLength={400} onChange={(e) => set("registration", e.target.value)} />
              </Field>

              <Field label="Logo" hint="A PNG, JPEG, WebP or SVG, under 400 KB. It is drawn into the document itself.">
                <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
                  <input
                    ref={file}
                    type="file"
                    accept={LOGO_TYPES.join(",")}
                    style={{ display: "none" }}
                    onChange={(e) => {
                      const picked = e.target.files?.[0];
                      if (picked) onLogo(picked);
                      e.target.value = "";
                    }}
                  />
                  <Button onClick={() => file.current?.click()} icon="upload">
                    {draft.logo ? "Choose a different one" : "Choose an image"}
                  </Button>
                  {draft.logo ? (
                    <Button variant="quiet" onClick={() => set("logo", null)}>
                      Remove it
                    </Button>
                  ) : null}
                </div>
              </Field>

              <Field label="Your colour" hint="Used for headings and totals. Leave it off to keep the look's own colour.">
                <div className="row" style={{ gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <input
                    type="color"
                    className="input"
                    style={{ width: 56, padding: 3, height: 34 }}
                    value={draft.colour ?? looks.find((l) => l.id === draft.themeId)?.colour ?? "#1f3a5f"}
                    onChange={(e) => set("colour", e.target.value.toUpperCase())}
                    aria-label="Your brand colour"
                  />
                  <span className="small muted">{draft.colour ?? "Not set"}</span>
                  {draft.colour ? (
                    <Button variant="quiet" onClick={() => set("colour", null)}>
                      Clear
                    </Button>
                  ) : null}
                </div>
              </Field>

              <Field label="Look" hint="Each design comes with a look that suits it. Choose one here to use it for all of them.">
                <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
                  <button type="button" className={`tile${draft.themeId === null ? " tile--chosen" : ""}`} style={{ padding: "8px 12px", width: "auto" }} onClick={() => set("themeId", null)}>
                    <span className="small">Each design's own</span>
                  </button>
                  {looks.map((look) => (
                    <button key={look.id} type="button" className={`tile${draft.themeId === look.id ? " tile--chosen" : ""}`} style={{ padding: "8px 12px", width: "auto" }} onClick={() => set("themeId", look.id)}>
                      <div className="row" style={{ gap: 6, alignItems: "center" }}>
                        <span style={{ width: 12, height: 12, borderRadius: 3, background: look.colour, display: "inline-block" }} />
                        <span className="small">{look.name}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </Field>

              {!draft.isDefault ? (
                <Field label="New designs" hint="Which set of details a design starts with.">
                  <Button onClick={() => set("isDefault", true)}>Use this one for new designs</Button>
                </Field>
              ) : (
                <Note tone="info">New designs start with this set of details.</Note>
              )}
            </div>
            <div className="card__foot row" style={{ gap: 8, justifyContent: "space-between" }}>
              <Button variant="primary" onClick={() => void save()} busy={saving} icon="check">
                Save
              </Button>
              {brands.length > 1 ? (
                <Button variant="quiet" icon="trash" onClick={() => setDeleting(true)}>
                  Delete
                </Button>
              ) : null}
            </div>
          </Card>

          {/* The preview. The same block the starters draw, so this is what the
              document will say rather than an impression of it. */}
          <Card flat>
            <div className="card__body stack" style={{ gap: 10 }}>
              <span className="eyebrow">On the page</span>
              <div
                style={{
                  background: "var(--white)",
                  border: "1px solid var(--rule)",
                  borderRadius: 4,
                  padding: 22,
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                  fontFamily: "var(--font-body)"
                }}
              >
                {draft.logo ? <img src={draft.logo} alt="" style={{ height: 34, width: "auto", maxWidth: "60%", objectFit: "contain", alignSelf: "flex-start" }} /> : null}
                <div className="stack" style={{ gap: 2 }}>
                  <strong style={{ fontSize: 14, color: draft.colour ?? undefined }}>{draft.company || "Your business name"}</strong>
                  {draft.address ? <span className="small muted" style={{ whiteSpace: "pre-line" }}>{draft.address}</span> : null}
                  {draft.phone ? <span className="small muted">{draft.phone}</span> : null}
                  {draft.email ? <span className="small muted">{draft.email}</span> : null}
                  {draft.website ? <span className="small muted">{draft.website}</span> : null}
                </div>
                <div style={{ height: 1, background: "var(--rule)", margin: "6px 0" }} />
                <div className="stack" style={{ gap: 3 }}>
                  <span className="small muted" style={{ textTransform: "uppercase", letterSpacing: "0.06em", fontSize: 10 }}>
                    Total due
                  </span>
                  <strong style={{ fontFamily: "var(--font-display)", fontSize: 26, color: draft.colour ?? undefined }}>£2,442.00</strong>
                </div>
                {draft.registration ? (
                  <span className="small muted" style={{ fontSize: 10, marginTop: 8, whiteSpace: "pre-line" }}>
                    {draft.registration}
                  </span>
                ) : null}
              </div>
              <p className="small muted">
                An impression of the head and foot of a document. The rest depends on which design you are making.
              </p>
            </div>
          </Card>
        </div>
      ) : (
        <Note tone="ask" title="No business details yet">
          Press “Add another” and fill in your name and address. Every document you make will use them.
        </Note>
      )}

      {deleting && draft ? (
        <Confirm
          title={`Delete “${draft.name}”?`}
          confirmLabel="Delete it"
          onCancel={() => setDeleting(false)}
          onConfirm={async () => {
            setDeleting(false);
            try {
              await api.deleteBrand(draft.id);
              const { brands: loaded } = await api.brands();
              setBrands(loaded);
              const next = loaded.find((b) => b.isDefault) ?? loaded[0] ?? null;
              setChosen(next?.id ?? null);
              setDraft(next ?? null);
              toast("Deleted.", "good");
            } catch (e) {
              reportFailure(e, "We couldn't delete that.");
            }
          }}
        >
          Any design using it goes back to your default details. Documents you have already made are not affected.
        </Confirm>
      ) : null}
    </div>
  );
}
